import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";

import _ from "lodash";

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { NavLink } from 'react-router-dom';
import { registerUser } from "../../../../services/auth";

import { vestResolver } from "@hookform/resolvers/vest";
import { grey } from "@mui/material/colors";
import validators from "./validators";

import CountrySelect from "../../../../components/Shared/CountrySelect";
import DashedBreakline from "../../../../components/Shared/DashedBreakline";

function BlacklistEmail({ blacklists, close }: any) {
  return (
    <>
      <Dialog open onClose={close}>
        <DialogTitle>Email Blacklist</DialogTitle>
        <Box sx={{ pb: 2, px: 3 }}>
          <DialogContentText>
            There email addresses are not eligible for business accounts. Please try a different email address or contact support for assistance.
          </DialogContentText>
        </Box>
        <DialogContent>
          {blacklists.map((item: string) => {
            return (
              <Typography key={item}>{item}</Typography>
            )
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default function RegisterBusinessForm({ blacklists, confirmedType, setConfirmedType }: any) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      person: "",
      email: "",
      country: "Vietnam",
      phone: "",
      company: "",
      purpose: "-",
      type: confirmedType
    },
    resolver: vestResolver(validators)
  });

  const [completed, setCompleted] = useState<string | null>(localStorage.getItem('account.business.completed') || null)
  const [showBlacklist, setShowBlacklist] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>("");

  const { mutateAsync, isLoading } = useMutation(registerUser);

  const handleRegisterUser = async (data: any) => {
    setErrorMessage(null);

    data.type = confirmedType

    // console.log(data)

    const response = await mutateAsync(data)
    if (response?.errors) return setErrorMessage(response?.errors?.[0]?.message);

    if (response.data?.created) {
      return [
        setCompleted(data.email),
        localStorage.setItem('account.business.completed', data.email)
      ]
    }
  };

  function BlacklistHref() {
    return (
      <Typography component="span" fontSize={12} sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline-block' }} onClick={() => setShowBlacklist(!showBlacklist)} >here</Typography>
    )
  }

  function resolveEmailValidation(email: string) {
    const extension = _.last(email.split("@"))?.trim() || ''

    // console.log(extension, blacklists, blacklists.includes(extension))

    if (blacklists.includes(extension)) {
      setError('email', { message: `email [${email}] you are submiting blocked. If there any mistake, please contact us for more information.` })
    } else {
      clearErrors('email')
    }
  }

  const delayedValidateEmail = useCallback(
    _.debounce((value) => resolveEmailValidation(value), 500),
    []
  );

  return (
    <>
      {completed ? (
        <Box>
          <Typography fontSize={14} sx={{ mb: 2 }}>Dear ({completed}),</Typography>
          <Typography fontSize={14} sx={{ mb: 3 }}>Our dedicated team will review your business review request and respond shortly. We appreciate your patience and look forward to connecting with you soon</Typography>
          <Typography fontSize={14} sx={{ mb: 0.25 }}>Regards,</Typography>
          <Typography fontSize={14}>VNETWORK JSC</Typography>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit(handleRegisterUser)}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 4, fontSize: 14 }}>{errorMessage}</Alert>
          )}

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <CountrySelect {...field} />
            )}
          />

          <Controller
            name="person"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                label="Contact Person (*)"
                error={!!errors.person}
                InputLabelProps={{ shrink: true }}
                helperText={errors.person?.message}
                sx={{ minWidth: "100%", "& input": { fontSize: 13 }, mt: 3 }}
              />
            )}
          />

          <Controller
            name="email"
            defaultValue=""
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <TextField
                {...field}
                onChange={(event) => {
                  return [
                    onChange(event.target?.value),
                    delayedValidateEmail(event.target?.value)
                  ]
                }}
                size="small"
                type="text"
                label="Email Business (*)"
                error={!!errors.email}
                InputLabelProps={{ shrink: true }}
                helperText={errors.email?.message || <Typography component="span" fontSize={12}>Spam and free emails are blocked. See our blacklist <BlacklistHref />.</Typography>}
                sx={{ minWidth: "100%", "& input": { fontSize: 13 }, mt: 3 }}
              />
            )}
          />

          <Controller
            name="phone"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                label="Phone Number (*)"
                error={!!errors.phone}
                InputLabelProps={{ shrink: true }}
                helperText={errors.phone?.message}
                sx={{ minWidth: "100%", "& input": { fontSize: 13 }, mt: 3 }}
              />
            )}
          />

          <Controller
            name="company"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                label="Company Name"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: "100%", "& input": { fontSize: 13 }, mt: 3 }}
              />
            )}
          />

          {showBlacklist && <BlacklistEmail blacklists={blacklists} close={() => setShowBlacklist(false)} />}

          <Box sx={{ mt: 3 }}>
            <Controller
              name="purpose"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="purpose">Other Purpose</InputLabel>
                  <Select
                    labelId="purpose"
                    label="Other Purpose"
                    size="small"
                    {...field}
                  >
                    <MenuItem value="-">
                      <Typography fontSize={14}>-</Typography>
                    </MenuItem>
                    <MenuItem value="Become a Reseller">
                      <Typography fontSize={14}>Become a Reseller</Typography>
                    </MenuItem>
                    <MenuItem value="Partnership Proposal">
                      <Typography fontSize={14}>Partnership Proposal</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <Button
            disabled={isLoading}
            type="submit"
            sx={{ mb: 1, mt: 3 }}
            fullWidth
            variant="contained"
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: grey[900] }} />
            ) : (
              <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>Submit Review</Typography>
            )}
          </Button>

          <Button fullWidth sx={{ bgcolor: grey[50], color: 'black ! important' }} onClick={() => setConfirmedType(null)}>
            <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>
              Back
            </Typography>
          </Button>

          <DashedBreakline text="Already have account?" />

          <Button component={NavLink} to="/auth/login" fullWidth sx={{ bgcolor: grey[50], color: 'black ! important' }}>
            <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>
              Customer Login
            </Typography>
          </Button>
        </Box>
      )}
    </>
  );
}
